import { Vec3, vec3 } from './vec3'

/** Matrice 3x3 immutabile, ordine per righe: [r0c0, r0c1, r0c2, r1c0, ...]. */
export type Mat3 = readonly number[]

export function mat3(
  a: number, b: number, c: number,
  d: number, e: number, f: number,
  g: number, h: number, i: number,
): Mat3 {
  return [a, b, c, d, e, f, g, h, i]
}

export const IDENTITY3: Mat3 = mat3(1, 0, 0, 0, 1, 0, 0, 0, 1)
export const ZERO3: Mat3 = mat3(0, 0, 0, 0, 0, 0, 0, 0, 0)

export const diagonal = (x: number, y: number, z: number): Mat3 =>
  mat3(x, 0, 0, 0, y, 0, 0, 0, z)

export function mulVec3(m: Mat3, v: Vec3): Vec3 {
  return vec3(
    m[0]! * v.x + m[1]! * v.y + m[2]! * v.z,
    m[3]! * v.x + m[4]! * v.y + m[5]! * v.z,
    m[6]! * v.x + m[7]! * v.y + m[8]! * v.z,
  )
}

export function mulMat3(a: Mat3, b: Mat3): Mat3 {
  const out = new Array<number>(9)
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      out[r * 3 + c] = a[r * 3]! * b[c]! + a[r * 3 + 1]! * b[3 + c]! + a[r * 3 + 2]! * b[6 + c]!
    }
  }
  return out
}

export const transpose = (m: Mat3): Mat3 =>
  mat3(m[0]!, m[3]!, m[6]!, m[1]!, m[4]!, m[7]!, m[2]!, m[5]!, m[8]!)

export const addMat3 = (a: Mat3, b: Mat3): Mat3 => a.map((v, i) => v + b[i]!)
export const scaleMat3 = (m: Mat3, s: number): Mat3 => m.map((v) => v * s)

/** Prodotto esterno a ⊗ b, serve al teorema di Huygens-Steiner. */
export const outer = (a: Vec3, b: Vec3): Mat3 =>
  mat3(a.x * b.x, a.x * b.y, a.x * b.z,
       a.y * b.x, a.y * b.y, a.y * b.z,
       a.z * b.x, a.z * b.y, a.z * b.z)

export function determinant(m: Mat3): number {
  return (
    m[0]! * (m[4]! * m[8]! - m[5]! * m[7]!) -
    m[1]! * (m[3]! * m[8]! - m[5]! * m[6]!) +
    m[2]! * (m[3]! * m[7]! - m[4]! * m[6]!)
  )
}

export function inverse(m: Mat3): Mat3 {
  const det = determinant(m)
  if (det === 0) throw new Error('Mat3 non invertibile: determinante nullo')
  const id = 1 / det
  return mat3(
    (m[4]! * m[8]! - m[5]! * m[7]!) * id,
    (m[2]! * m[7]! - m[1]! * m[8]!) * id,
    (m[1]! * m[5]! - m[2]! * m[4]!) * id,
    (m[5]! * m[6]! - m[3]! * m[8]!) * id,
    (m[0]! * m[8]! - m[2]! * m[6]!) * id,
    (m[2]! * m[3]! - m[0]! * m[5]!) * id,
    (m[3]! * m[7]! - m[4]! * m[6]!) * id,
    (m[1]! * m[6]! - m[0]! * m[7]!) * id,
    (m[0]! * m[4]! - m[1]! * m[3]!) * id,
  )
}
